import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Skeleton,
} from '@/components'
import { cn } from '@/lib/utils'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo, useState } from 'react'
import { useTheme } from 'styled-components'
import { SALARY_STATISTICS } from '../queries'

type SalaryStatisticsUser = {
  id: string
  fullName: string
}

interface SalaryStatisticsProps {
  year: number
  month: number
  onUserClick: (user: SalaryStatisticsUser) => void
}

interface UserSegmentProps {
  title: string
  users: SalaryStatisticsUser[]
  total: number
  color: string
  disabled?: boolean
  onUserClick: (user: SalaryStatisticsUser) => void
}

function UserSegment({
  title,
  users,
  total,
  color,
  disabled = false,
  onUserClick,
}: UserSegmentProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const translations = useTranslate({
    noData: 'common.no-data',
    noResults: 'common.no-results',
    search: 'common.search',
  })

  const percent = total > 0 ? Math.round((users.length / total) * 100) : 0
  const visibleUsers = useMemo(() => {
    const searchTerm = search.trim().toLowerCase()
    if (!searchTerm) return users

    return users.filter(user =>
      user.fullName.toLowerCase().includes(searchTerm)
    )
  }, [search, users])

  function handleOpenChange(open: boolean) {
    setOpen(open)
    if (!open) setSearch('')
  }

  function handleUserClick(user: SalaryStatisticsUser) {
    onUserClick(user)
    setOpen(false)
    setSearch('')
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button
          aria-label={title}
          className={cn(
            'flex min-w-0 items-center justify-center px-3 text-sm font-semibold text-white transition-opacity',
            !disabled && 'hover:opacity-85',
            disabled && 'cursor-default opacity-60'
          )}
          disabled={disabled}
          style={{
            backgroundColor: color,
            flexGrow: users.length,
            flexBasis: users.length ? 0 : '0%',
          }}
          type="button"
        >
          <span className="truncate">
            {users.length} ({percent}%)
          </span>
        </button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {title} ({users.length})
          </DialogTitle>
          <DialogDescription className="sr-only">{title}</DialogDescription>
        </DialogHeader>

        {!!users.length && (
          <Input
            type="search"
            className="h-10 rounded-md text-md"
            placeholder={translations.search}
            value={search}
            onChange={setSearch}
          />
        )}

        {visibleUsers.length ? (
          <div className="grid max-h-[60vh] gap-2 overflow-y-auto pr-1">
            {visibleUsers.map(user => (
              <Button
                key={user.id}
                className="w-full justify-start"
                size="sm"
                variant="ghost"
                onClick={() => handleUserClick(user)}
              >
                {user.fullName}
              </Button>
            ))}
          </div>
        ) : users.length ? (
          <p className="m-0 text-gray-500">{translations.noResults}</p>
        ) : (
          <p className="m-0 text-gray-500">{translations.noData}</p>
        )}
      </DialogContent>
    </Dialog>
  )
}

export function SalaryStatistics({
  year,
  month,
  onUserClick,
}: SalaryStatisticsProps) {
  const translations = useTranslate({
    salaryStatistics: 'salary.statistics',
    salaryStatisticsError: 'error.generic-server-error',
    usersWithExportedSalaryBaseEntries:
      'salary.users-with-exported-salary-base-entries',
    usersWithUnexportedSalaryBaseEntries:
      'salary.users-with-unexported-salary-base-entries',
    usersMissingSalaryBaseEntries: 'salary.users-missing-salary-base-entries',
    totalN: ['common.total-n', { n: 0 }],
  })
  const theme = useTheme()

  const { loading, error, data } = useQuery(SALARY_STATISTICS, {
    variables: { year, month },
    fetchPolicy: 'cache-and-network',
  })

  const missingUsers = data?.allUsersMissingSalaryBaseEntries ?? []
  const unexportedUsers = data?.allUsersWithUnexportedSalaryBaseEntries ?? []
  const exportedUsers = data?.allUsersWithExportedSalaryBaseEntries ?? []
  const total =
    missingUsers.length + unexportedUsers.length + exportedUsers.length

  if (loading && !data) {
    return <Skeleton className="mb-4 h-20 w-full rounded-xl" />
  }

  if (error) {
    return (
      <Card className="mb-4 px-5 py-4 text-red-700">
        {translations.salaryStatisticsError}
      </Card>
    )
  }

  return (
    <Card className="mb-4 gap-3 px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="m-0 text-lg font-semibold">
            {translations.salaryStatistics}
          </h2>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
            <span
              className="font-semibold"
              style={{ color: theme.colors.green }}
            >
              {translations.usersWithExportedSalaryBaseEntries}:{' '}
              {exportedUsers.length}
            </span>
            <span
              className="font-semibold"
              style={{ color: theme.colors.orange }}
            >
              {translations.usersWithUnexportedSalaryBaseEntries}:{' '}
              {unexportedUsers.length}
            </span>
            <span
              className="font-semibold"
              style={{ color: theme.colors.red8 }}
            >
              {translations.usersMissingSalaryBaseEntries}:{' '}
              {missingUsers.length}
            </span>
          </div>
        </div>

        <span className="text-sm font-semibold text-gray-500">
          {translations.totalN({ n: total })}
        </span>
      </div>

      <div className="flex h-9 overflow-hidden rounded-md bg-gray-200">
        {total === 0 ? (
          <div className="flex flex-1 items-center justify-center text-sm font-semibold text-gray-500">
            0
          </div>
        ) : (
          <>
            {!!exportedUsers.length && (
              <UserSegment
                title={translations.usersWithExportedSalaryBaseEntries}
                users={exportedUsers}
                total={total}
                color={theme.colors.green}
                onUserClick={onUserClick}
              />
            )}
            {!!unexportedUsers.length && (
              <UserSegment
                title={translations.usersWithUnexportedSalaryBaseEntries}
                users={unexportedUsers}
                total={total}
                color={theme.colors.orange}
                onUserClick={onUserClick}
              />
            )}
            {!!missingUsers.length && (
              <UserSegment
                title={translations.usersMissingSalaryBaseEntries}
                users={missingUsers}
                total={total}
                color={theme.colors.red8}
                onUserClick={onUserClick}
              />
            )}
          </>
        )}
      </div>
    </Card>
  )
}
